import React from 'react';

const TermsAndConditions: React.FC = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-[#F0F9FF] via-white to-[#FFF1F2]">
      {/* Header */}
      <div className="bg-white border-b border-[#334155]/10">
        <div className="max-w-4xl mx-auto px-6 py-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-[#334155] mb-2">
                Terms & Conditions
              </h1>
              <p className="text-[#334155]/60">
                Last updated: January 2026
              </p>
            </div>
            <button
              onClick={() => window.location.href = '/'}
              className="px-4 py-2 text-[#334155] hover:bg-[#F0F9FF] rounded-lg transition-colors"
            >
              ← Back to Home
            </button>
          </div>
        </div>
      </div>

      {/* Terms Content */}
      <div className="max-w-4xl mx-auto px-6 py-12">
        <div className="bg-white rounded-2xl shadow-sm border border-[#334155]/10 p-8 md:p-12 space-y-8">
          
          {/* Introduction */}
          <section>
            <h2 className="text-2xl font-semibold text-[#334155] mb-4">
              1. Introduction
            </h2>
            <div className="text-[#334155]/70 leading-relaxed space-y-3">
              <p>
                Welcome to SheCare. By accessing or using our platform, you agree to be bound by these Terms and Conditions.
              </p>
              <p>
                SheCare provides online health consultations for educational and guidance purposes. Please read these terms carefully before using our services.
              </p>
            </div>
          </section>

          {/* Scope of Services */}
          <section>
            <h2 className="text-2xl font-semibold text-[#334155] mb-4">
              2. Scope of Services
            </h2>
            <div className="text-[#334155]/70 leading-relaxed space-y-3">
              <p>
                SheCare provides online consultations for educational and guidance purposes. SheCare does not replace in-person medical care, emergency services, or long-term treatment.
              </p>
              <p>
                Our consultations are designed to help you understand your health concerns, provide reassurance, and guide you on appropriate next steps — including when to seek local medical care.
              </p>
            </div>
          </section>

          {/* No Emergency Service */}
          <section>
            <h2 className="text-2xl font-semibold text-[#334155] mb-4">
              3. No Emergency Service
            </h2>
            <div className="bg-[#FFF1F2] border border-[#FDA4AF]/30 rounded-xl p-6">
              <p className="text-[#334155] leading-relaxed">
                <strong>Important:</strong> SheCare is not intended for medical emergencies. Users experiencing emergencies must seek immediate local medical care.
              </p>
              <p className="text-[#334155]/70 leading-relaxed mt-3">
                If you are experiencing severe pain, heavy bleeding, chest pain, difficulty breathing, or any other medical emergency, please call your local emergency services or go to the nearest hospital immediately.
              </p>
            </div>
          </section>

          {/* Medical Disclaimer */}
          <section>
            <h2 className="text-2xl font-semibold text-[#334155] mb-4">
              4. Medical Disclaimer
            </h2>
            <div className="text-[#334155]/70 leading-relaxed space-y-3">
              <p>
                Information provided during consultations is not a substitute for physical examination, diagnosis, or treatment by a local healthcare provider.
              </p>
              <p>
                SheCare consultations are intended to provide guidance and education. They do not constitute a doctor-patient relationship for the purposes of diagnosis, prescription, or ongoing treatment.
              </p>
              <p>
                You should always consult with a local healthcare provider for physical examinations, diagnostic tests, prescriptions, and ongoing medical care.
              </p>
            </div>
          </section>

          {/* User Responsibilities */}
          <section>
            <h2 className="text-2xl font-semibold text-[#334155] mb-4">
              5. User Responsibilities
            </h2>
            <div className="text-[#334155]/70 leading-relaxed space-y-3">
              <p>As a user of SheCare, you agree to:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Provide accurate and complete information during consultations</li>
                <li>Use the service for its intended purpose of guidance and education</li>
                <li>Seek appropriate local medical care when advised or needed</li>
                <li>Not use the service for emergencies or urgent medical situations</li>
                <li>Respect the professional advice and guidance provided by doctors</li>
                <li>Maintain the confidentiality of your account credentials</li>
              </ul>
            </div>
          </section>

          {/* Doctor Responsibilities */}
          <section>
            <h2 className="text-2xl font-semibold text-[#334155] mb-4">
              6. Doctor Responsibilities
            </h2>
            <div className="text-[#334155]/70 leading-relaxed space-y-3">
              <p>
                All doctors on SheCare are licensed professionals who agree to provide consultations within the scope of their licensing and expertise.
              </p>
              <p>
                Doctors will provide guidance, education, and recommendations based on the information shared during consultations. They will advise when in-person medical care is necessary.
              </p>
            </div>
          </section>

          {/* Booking, Payments & Refunds */}
          <section>
            <h2 className="text-2xl font-semibold text-[#334155] mb-4">
              7. Booking, Payments & Refunds
            </h2>
            <div className="text-[#334155]/70 leading-relaxed space-y-3">
              <p>
                Consultation fees are clearly displayed before booking. Payment is required at the time of booking.
              </p>
              <p>
                Refunds may be provided in cases where:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>The consultation could not be completed due to technical issues on our end</li>
                <li>The doctor was unable to attend the scheduled consultation</li>
                <li>Other circumstances as determined by SheCare on a case-by-case basis</li>
              </ul>
              <p>
                Refund requests should be submitted within 7 days of the consultation date.
              </p>
            </div>
          </section>

          {/* Jurisdiction & Licensing */}
          <section>
            <h2 className="text-2xl font-semibold text-[#334155] mb-4">
              8. Jurisdiction & Licensing
            </h2>
            <div className="text-[#334155]/70 leading-relaxed space-y-3">
              <p>
                The scope of services may vary depending on the location of the user and the licensing of the consulting doctor.
              </p>
              <p>
                SheCare doctors are licensed in their respective jurisdictions. The ability to provide certain services (such as prescriptions or formal diagnoses) may be limited by local regulations and licensing requirements.
              </p>
              <p>
                Users are responsible for understanding and complying with local healthcare regulations in their country of residence.
              </p>
            </div>
          </section>

          {/* Limitation of Liability */}
          <section>
            <h2 className="text-2xl font-semibold text-[#334155] mb-4">
              9. Limitation of Liability
            </h2>
            <div className="text-[#334155]/70 leading-relaxed space-y-3">
              <p>
                SheCare provides a platform for educational consultations. We make reasonable efforts to ensure quality service, but we cannot guarantee specific outcomes.
              </p>
              <p>
                To the fullest extent permitted by law, SheCare and its doctors shall not be liable for any indirect, incidental, special, or consequential damages arising from the use of our services.
              </p>
              <p>
                Users acknowledge that online consultations have inherent limitations compared to in-person medical care.
              </p>
            </div>
          </section>

          {/* Privacy & Data Protection */}
          <section>
            <h2 className="text-2xl font-semibold text-[#334155] mb-4">
              10. Privacy & Data Protection
            </h2>
            <div className="text-[#334155]/70 leading-relaxed space-y-3">
              <p>
                We take your privacy seriously. All personal and medical information is handled securely and confidentially in accordance with our Privacy Policy.
              </p>
              <p>
                By using SheCare, you consent to the collection, use, and storage of your information as described in our Privacy Policy.
              </p>
            </div>
          </section>

          {/* Termination */}
          <section>
            <h2 className="text-2xl font-semibold text-[#334155] mb-4">
              11. Termination
            </h2>
            <div className="text-[#334155]/70 leading-relaxed space-y-3">
              <p>
                SheCare reserves the right to suspend or terminate access to our services for users who violate these Terms and Conditions or misuse the platform.
              </p>
              <p>
                Users may close their accounts at any time by contacting our support team.
              </p>
            </div>
          </section>

          {/* Governing Law */}
          <section>
            <h2 className="text-2xl font-semibold text-[#334155] mb-4">
              12. Governing Law
            </h2>
            <div className="text-[#334155]/70 leading-relaxed space-y-3">
              <p>
                These Terms shall be governed by and construed in accordance with the laws of Singapore.
              </p>
              <p>
                Any disputes arising from these Terms or the use of SheCare services shall be subject to the exclusive jurisdiction of the courts of Singapore.
              </p>
            </div>
          </section>

          {/* Changes to Terms */}
          <section>
            <h2 className="text-2xl font-semibold text-[#334155] mb-4">
              13. Changes to Terms
            </h2>
            <div className="text-[#334155]/70 leading-relaxed space-y-3">
              <p>
                SheCare reserves the right to update these Terms and Conditions at any time. Users will be notified of significant changes.
              </p>
              <p>
                Continued use of the platform after changes constitutes acceptance of the updated Terms.
              </p>
            </div>
          </section>

          {/* Contact */}
          <section className="pt-6 border-t border-[#334155]/10">
            <h2 className="text-2xl font-semibold text-[#334155] mb-4">
              Contact Us
            </h2>
            <div className="text-[#334155]/70 leading-relaxed">
              <p>
                If you have questions about these Terms and Conditions, please contact us through our platform or visit our <a href="/faq" className="text-[#38BDF8] hover:text-[#0EA5E9] transition-colors">FAQ page</a>.
              </p>
            </div>
          </section>

        </div>

        {/* Acceptance Notice */}
        <div className="mt-8 bg-[#F0F9FF] border border-[#38BDF8]/20 rounded-xl p-6">
          <p className="text-[#334155] text-center">
            By using SheCare, you acknowledge that you have read, understood, and agree to be bound by these Terms and Conditions.
          </p>
        </div>
      </div>

      {/* Footer */}
      <div className="bg-white border-t border-[#334155]/10 py-6 mt-12">
        <div className="max-w-4xl mx-auto px-6 text-center text-sm text-[#334155]/50">
          <p>
            <a href="/faq" className="hover:text-[#38BDF8] transition-colors">FAQ</a>
            {' • '}
            <a href="/privacy" className="hover:text-[#38BDF8] transition-colors">Privacy Policy</a>
            {' • '}
            <a href="/" className="hover:text-[#38BDF8] transition-colors">Home</a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default TermsAndConditions;
